import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { Button as GrommetButton, ButtonExtendedProps, Text } from 'grommet';
import { LoaderCircleIcon } from 'lucide-preact';
import { ComponentProps, createContext, HTMLAttributes, ReactNode, useContext } from 'preact/compat';

import { cn } from '@/lib/utils';

import style from './style.scss';

export interface ZLButtonProps extends Omit<ButtonExtendedProps, 'loading'> {
  loading?: boolean;
}

const getLabel = (loading: boolean | string | undefined, label: ReactNode): string | ReactNode => {
  if (loading && typeof loading === 'boolean') {
    return <LoaderCircleIcon className="size-[1em] animate-spin" style={{ verticalAlign: 'middle', fontSize: '16px' }} />;
  }
  if (loading && typeof loading === 'string') {
    return loading;
  }

  return label;
};

export const PrimaryButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    primary
    size={size}
    className={clsx(className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const OutlineButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(className, style[size], style.outline, { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);
export const NewOutlineButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(className, style[size], style.newOutlineButton, { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const OutlineButtonDanger = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(style.danger, className, style[size], style.outline, { [style.loading]: loading })}
    label={<Text color="var(--red)">{getLabel(loading, label as string) as string}</Text>}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const PrimaryYellow = ({ loading, label, size = 'large', type, className, ...args }: ZLButtonProps) => (
  <GrommetButton
    type={type}
    size={size}
    className={clsx(style.yellow, className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label ?? '')}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const PrimarySecondaryGreen = ({ loading, label, size = 'large', type, className, ...args }: ZLButtonProps) => (
  <GrommetButton
    type={type}
    size={size}
    className={clsx(style.secondaryGreen, className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const LandingPrimary = ({ className, children, type = 'button', ...rest }: HTMLAttributes<HTMLButtonElement>) => (
  // eslint-disable-next-line react/button-has-type, react/jsx-props-no-spreading
  <button type={type} className={clsx(style.landingPrimary, className)} {...rest}>
    {children}
  </button>
);

export const LandingSecondary = ({ className, children, type = 'button', ...rest }: HTMLAttributes<HTMLButtonElement>) => (
  // eslint-disable-next-line react/button-has-type, react/jsx-props-no-spreading
  <button type={type} className={clsx(style.landingSecondary, className)} {...rest}>
    {children}
  </button>
);

/** Single source of truth for size-linked shell, content, spinner, and icon styles. */
const buttonSizeScale = {
  xs: {
    shell: "px-3 py-2 text-xs font-semibold [&_svg:not([class*='size-'])]:size-4",
    content: '',
    spinner: 'size-4',
    icon: "size-7 text-xs font-semibold p-0 [&_svg:not([class*='size-'])]:size-4",
  },
  sm: {
    shell: "px-3 py-2 text-sm font-semibold [&_svg:not([class*='size-'])]:size-4",
    content: '',
    spinner: 'size-4',
    icon: "size-9 text-sm font-semibold p-0 [&_svg:not([class*='size-'])]:size-4",
  },
  default: {
    shell: "px-5 py-2.5 text-sm font-semibold [&_svg:not([class*='size-'])]:size-4.5",
    content: '',
    spinner: 'size-5',
    icon: "size-[42px] text-sm font-semibold p-0 [&_svg:not([class*='size-'])]:size-4.5",
  },
  lg: {
    shell: "px-5 py-3 font-medium text-base [&_svg:not([class*='size-'])]:size-5",
    content: 'gap-3',
    spinner: 'size-5',
    icon: "size-12 font-medium text-base p-0 [&_svg:not([class*='size-'])]:size-5",
  },
  xl: {
    shell: "px-6 py-3.5 font-medium text-base [&_svg:not([class*='size-'])]:size-5",
    content: 'gap-3',
    spinner: 'size-6',
    icon: "size-[52px] font-medium text-base p-0 [&_svg:not([class*='size-'])]:size-5",
  },
} as const;

type ButtonTextSize = keyof typeof buttonSizeScale;

type ButtonSize = ButtonTextSize | 'icon-xs' | 'icon-sm' | 'icon' | 'icon-lg' | 'icon-xl';

const iconSizeToTextSize: Record<Exclude<ButtonSize, ButtonTextSize>, ButtonTextSize> = {
  'icon-xs': 'xs',
  'icon-sm': 'sm',
  icon: 'default',
  'icon-lg': 'lg',
  'icon-xl': 'xl',
};

function resolveTextSize(size: ButtonSize | null | undefined): ButtonTextSize {
  if (!size) {
    return 'default';
  }

  if (size in iconSizeToTextSize) {
    return iconSizeToTextSize[size as keyof typeof iconSizeToTextSize];
  }

  if (size in buttonSizeScale) {
    return size as ButtonTextSize;
  }

  return 'default';
}

const buttonShellSizes: Record<ButtonSize, string> = {
  xs: buttonSizeScale.xs.shell,
  sm: buttonSizeScale.sm.shell,
  default: buttonSizeScale.default.shell,
  lg: buttonSizeScale.lg.shell,
  xl: buttonSizeScale.xl.shell,
  'icon-xs': buttonSizeScale.xs.icon,
  'icon-sm': buttonSizeScale.sm.icon,
  icon: buttonSizeScale.default.icon,
  'icon-lg': buttonSizeScale.lg.icon,
  'icon-xl': buttonSizeScale.xl.icon,
};

export const buttonVariants = cva(
  'group/button focus-visible:ring-ring/50 inline-flex shrink-0 items-center justify-center border border-transparent outline-none transition-all focus-visible:ring-[3px] aria-busy:pointer-events-none aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'rounded-full bg-forest-core text-white hover:bg-forest-medium focus-visible:bg-forest-medium focus-visible:ring-forest-core aria-disabled:bg-stone-300',
        destructive:
          'rounded-full bg-rose-700 text-white hover:bg-rose-600 focus-visible:bg-rose-600 focus-visible:ring-rose-700',
        outline:
          'rounded-full border-forest-core bg-transparent text-forest-core hover:bg-tea-50 focus-visible:border-transparent focus-visible:ring-forest-light aria-disabled:border-stone-300 aria-disabled:text-stone-300',
        link: 'rounded-[10px] text-stone-500 underline underline-offset-4 hover:text-stone-700 focus-visible:text-stone-900 aria-disabled:text-stone-300',
        tertiary:
          'rounded-full bg-sun-400 text-forest-darkest hover:bg-sun-200 focus-visible:bg-sun-200 focus-visible:ring-[#FFD300] aria-disabled:bg-stone-300 aria-disabled:text-white',
        ghost:
          'rounded-full text-stone-500 hover:bg-stone-200 hover:text-stone-900 focus-visible:ring-stone-500/30 aria-disabled:text-stone-300',
        secondary:
          'rounded-full border-stone-300 bg-stone-50 text-stone-700 hover:bg-stone-100 focus-visible:ring-stone-400/30 aria-disabled:border-stone-300 aria-disabled:text-stone-300',
        glass:
          'rounded-full border-2 border-neutral-200/50 bg-neutral-400/50 text-white backdrop-blur-[2px] hover:border-neutral-200 hover:bg-neutral-400 focus-visible:ring-white/30',
        bordered:
          'hover:border-white/99 rounded-full border-2 border-white/50 bg-forest-core text-white hover:bg-forest-medium focus-visible:bg-forest-medium focus-visible:ring-forest-core aria-disabled:bg-stone-300',
        mediaFilter: 'rounded-full bg-[#F1F1F1] text-stone-500 hover:opacity-50',
        plain: '',
      },
      size: buttonShellSizes,
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const buttonContentVariants = cva('group/button-content inline-flex items-center justify-center gap-2 whitespace-nowrap', {
  variants: {
    size: {
      xs: buttonSizeScale.xs.content,
      sm: buttonSizeScale.sm.content,
      default: buttonSizeScale.default.content,
      lg: buttonSizeScale.lg.content,
      xl: buttonSizeScale.xl.content,
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

function getLoadingSpinnerClassName(size: ButtonTextSize) {
  return cn(buttonSizeScale[size].spinner, 'text-current');
}

type ButtonContentProps = ComponentProps<'span'> &
  VariantProps<typeof buttonContentVariants> & {
    loading?: boolean;
    containerClassName?: string;
  };

const ButtonContentContext = createContext<{
  loading?: boolean;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: ButtonTextSize;
}>({});

function ButtonContent({
  className,
  containerClassName,
  size: sizeProp,
  loading: loadingProp,
  children,
  ...props
}: ButtonContentProps) {
  const context = useContext(ButtonContentContext);
  const loading = loadingProp ?? context.loading ?? false;
  const size = sizeProp ?? context.size ?? 'default';

  return (
    <div data-slot="button-content" className={cn('relative flex', containerClassName)}>
      <span
        className={cn(buttonContentVariants({ size }), loading && 'invisible', className)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </span>
      {loading ? (
        <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <LoaderCircleIcon className={cn('size-[1em]', getLoadingSpinnerClassName(size), 'animate-spin')} />
        </span>
      ) : null}
    </div>
  );
}

type ButtonVariantProps = Omit<VariantProps<typeof buttonVariants>, 'size'> & {
  size?: ButtonSize;
};

type SharedButtonProps = ButtonVariantProps & {
  className?: string;
  loading?: boolean;
  plainContent?: boolean;
  children?: ReactNode;
};

function useButtonShell({
  className,
  variant,
  size,
  loading = false,
  plainContent = false,
  disabled = false,
  children,
}: SharedButtonProps & { disabled?: boolean }) {
  const contentSize = resolveTextSize(size);
  const content = plainContent ? children : <ButtonContent>{children}</ButtonContent>;

  return {
    contentSize,
    content,
    shellProps: {
      'data-slot': 'button' as const,
      'data-loading': loading || undefined,
      'aria-disabled': disabled || undefined,
      'aria-busy': loading || undefined,
      className: cn(buttonVariants({ variant, size, className })),
    },
    contextValue: { loading, variant, size: contentSize },
  };
}

export const Button = ({
  className,
  variant,
  size,
  loading = false,
  plainContent = false,
  disabled = false,
  children,
  ...props
}: Omit<ComponentProps<'button'>, 'size' | 'children' | 'loading' | 'disabled'> &
  SharedButtonProps & {
    children?: ReactNode;
    disabled?: boolean;
  }) => {
  const { content, shellProps, contextValue } = useButtonShell({
    className,
    variant,
    size,
    loading,
    plainContent,
    disabled,
    children,
  });

  return (
    <ButtonContentContext.Provider value={contextValue}>
      {/* eslint-disable-next-line react/button-has-type */}
      <button
        type="button"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...shellProps}
        disabled={disabled || loading || undefined}
        className={cn('preflight preflight-button', shellProps.className)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {content}
      </button>
    </ButtonContentContext.Provider>
  );
};

export const ButtonLink = ({
  className,
  variant,
  size,
  loading = false,
  plainContent = false,
  disabled = false,
  children,
  ...props
}: Omit<ComponentProps<'a'>, 'size' | 'children' | 'loading' | 'disabled'> &
  SharedButtonProps & {
    children?: ReactNode;
    disabled?: boolean;
  }) => {
  const { content, shellProps, contextValue } = useButtonShell({
    className,
    variant,
    size,
    loading,
    plainContent,
    disabled,
    children,
  });

  return (
    <ButtonContentContext.Provider value={contextValue}>
      <a
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...shellProps}
        className={cn('preflight preflight-a', shellProps.className)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {content}
      </a>
    </ButtonContentContext.Provider>
  );
};

export type { ButtonSize, ButtonTextSize };
export { ButtonContent, buttonContentVariants, buttonSizeScale, resolveTextSize };
